import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapImageView } from '../../components/world/MapImageView';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { SpawnRow } from '../../components/world/SpawnRow';
import { EventChip } from '../../components/world/EventChip';
import { Disclosure, StatList } from '../../components/world/Disclosure';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { useHashScroll } from '../../hooks/useHashScroll';
import { documentedAccessLevels, monsterName, monsterPath, prettyAccessName } from '../../utils/worldFormat';
import type { WorldData, WorldMap } from '../../types/world';
type Presence = 'todo' | 'permanente' | 'temporal';
export function MapDetail() {
  const {
    mapId
  } = useParams();
  useHashScroll();
  return <WorldGate data-id="element-2585" data-mp="2pg1hpyjhxnw8">
      {w => {
      const m = w.maps.get(Number(mapId));
      if (!m) return <NotFound data-id="element-2586" data-mp="1meixvt1mv5fx" />;
      return <MapView w={w} m={m} data-id="element-2587" data-mp="1mcxzttg414db" />;
    }}
    </WorldGate>;
}
function NotFound() {
  return <div className="rounded-2xl border border-white/10 p-6" data-id="element-2588" data-mp="1qejb5aczxale">
      <p className="text-white" data-id="element-2589" data-mp="1v8ds4617vbgs">No encontramos ese mapa en los archivos.</p>
      <Link to="/guia/mapas" className="mt-3 inline-flex font-semibold text-[#D4AF37]" data-id="element-2590" data-mp="206yt47oalz6a">Volver a Mapas y spots</Link>
    </div>;
}
function MapView({
  w,
  m
}: {
  w: WorldData;
  m: WorldMap;
}) {
  const [presence, setPresence] = useState<Presence>('todo');
  useEffect(() => {
    document.title = `${m.name} · Mapas · Guía MU Vanguard`;
  }, [m.name]);
  const spawns = w.idx.spawnsByMap.get(m.id) ?? [];
  const spots = useMemo(() => spawns.filter(s => s.kind === 'spot').sort((a, b) => (w.monsters.get(a.monsterId)?.level ?? 0) - (w.monsters.get(b.monsterId)?.level ?? 0) || a.monsterId - b.monsterId), [spawns, w]);
  const invasionSpawns = spawns.filter(s => s.kind === 'invasion');
  const locations = w.idx.locationsByMap.get(m.id) ?? [];
  const permanent = locations.filter(l => l.kind === 'ordinary');
  const temporary = locations.filter(l => l.kind !== 'ordinary');
  const eventIds = Array.from(new Set([...spawns, ...locations].map(x => x.eventId).filter((v): v is string => !!v)));
  const levels = documentedAccessLevels(m);
  const showPermanent = presence !== 'temporal';
  const showTemporary = presence !== 'permanente';
  const filters: {
    id: Presence;
    label: string;
  }[] = [{
    id: 'todo',
    label: 'Todo'
  }, {
    id: 'permanente',
    label: 'Permanente'
  }, {
    id: 'temporal',
    label: 'Eventos e invasiones'
  }];
  return <div data-id="element-2591" data-mp="1u8diwan198vs">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Mapas y spots',
      to: '/guia/mapas'
    }, {
      label: m.name
    }]} eyebrow="Mapa" title={m.name} data-id="element-2592" data-mp="1u83ugrhmz9s7">
        <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-2593" data-mp="11oop2xehtjg5">
          <span className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1.5 text-sm font-semibold text-[#F3E5AB]" data-id="element-2594" data-mp="wreul3c8yhlg">
            {levels.length ? `Acceso desde nivel ${Math.min(...levels)}` : 'Acceso especial'}
          </span>
          <span className="text-sm text-gray-400" data-id="element-2595" data-mp="3d69s3sei5tnk">{spots.length} spots · {new Set(locations.map(l => l.monsterId)).size} monstruos documentados</span>
          <CopyLinkButton label="Copiar enlace del mapa" data-id="element-2596" data-mp="33em8ues3nix6" />
        </div>
      </WorldPageHeader>

      <div className="space-y-10" data-id="element-2597" data-mp="3r486gxstpq32">
        <WorldSection id="accesos" title="Accesos y requisitos" data-id="element-2598" data-mp="3r61as6fyry7w">
          {m.moveEntries.length ? <ul className="divide-y divide-white/5 rounded-2xl border border-white/10" data-id="element-2599" data-mp="3w3b1dpf3afxb">
              {m.moveEntries.map(e => <li key={`${e.name}-${e.gateId ?? ''}`} className="flex items-center justify-between gap-4 px-4 py-3" data-id="element-2600" data-mp="3i6eefz23dilp">
                  <span className="text-white" data-id="element-2601" data-mp="26p4vreqseulg">{prettyAccessName(e.name)}</span>
                  <span className="text-sm tabular-nums text-gray-300" data-id="element-2602" data-mp="2aqi01ke8epq9">{e.minLevel !== undefined ? `Nivel ${e.minLevel}` : 'Nivel no documentado'}</span>
                </li>)}
            </ul> : <p className="text-gray-400" data-id="element-2603" data-mp="2m6e97tjp2koy">Sin accesos por movimiento documentados.</p>}
          {m.notes.length > 0 && <ul className="mt-3 space-y-1.5 text-sm text-gray-400" data-id="element-2604" data-mp="2h8l4l4y1hyqs">
              {m.notes.map(n => <li key={n} data-id="element-2605" data-mp="2c9rnz3nghrme">{n}</li>)}
            </ul>}
          <p className="mt-3 text-sm text-gray-500" data-id="element-2606" data-mp="39w8k45kgdz6k">Cada acceso tiene su propio requisito. Los spots no se asignan a una subzona concreta.</p>
        </WorldSection>

        <div role="group" aria-label="Filtrar presencia" className="flex flex-wrap gap-1.5" data-id="element-2607" data-mp="34y6zhg7dprsz">
          {filters.map(f => <button key={f.id} type="button" aria-pressed={presence === f.id} onClick={() => setPresence(f.id)} className={`h-11 rounded-full px-4 text-sm font-semibold transition-colors duration-150 ${presence === f.id ? 'bg-[#D4AF37] text-[#050b14]' : 'border border-white/15 text-gray-300 hover:text-white'}`} data-id="element-2608" data-mp="2vwpnoa108ca3">
              {f.label}
            </button>)}
        </div>

        <WorldSection id="imagen" title="Mapa" data-id="element-2609" data-mp="41d1l4soq3u8">
          <MapImageView w={w} mapId={m.id} spots={showPermanent ? spots : invasionSpawns} data-id="element-2610" data-mp="8zobffizcecg" />
        </WorldSection>

        {showPermanent && <WorldSection id="spots" title="Spots configurados" count={spots.length} data-id="element-2611" data-mp="ds7q6ftelfiu">
            {spots.length ? <ul className="grid gap-2.5" data-id="element-2612" data-mp="is3y2q581r3o">{spots.map(s => <SpawnRow key={s.id} w={w} s={s} show="monster" data-id="element-2613" data-mp="nob22de24siz" />)}</ul> : <p className="text-gray-400" data-id="element-2614" data-mp="rq64xmbmzdyv">No hay spots configurados en spawns.csv para este mapa.</p>}
          </WorldSection>}

        {showTemporary && <WorldSection id="invasiones" title="Apariciones de invasión" count={invasionSpawns.length} data-id="element-2615" data-mp="2ti2ewri5ofrj">
            {invasionSpawns.length ? <ul className="grid gap-2.5" data-id="element-2616" data-mp="2yhx48qma8xah">{invasionSpawns.map(s => <SpawnRow key={s.id} w={w} s={s} show="monster" data-id="element-2617" data-mp="5mnj1isgffay" />)}</ul> : <p className="text-gray-400" data-id="element-2618" data-mp="5oi1mqg96vbv">No hay apariciones de invasión configuradas en este mapa.</p>}
          </WorldSection>}

        <WorldSection id="monstruos" title="Monstruos presentes" data-id="element-2619" data-mp="eoeocmj31k4a">
          {(showPermanent ? permanent : []).length + (showTemporary ? temporary : []).length === 0 ? <p className="text-gray-400" data-id="element-2620" data-mp="okzqn03pwh4f">Sin presencia documentada en monster_locations.csv.</p> : <ul className="grid gap-2 sm:grid-cols-2" data-id="element-2621" data-mp="jn7yq632ov08">
              {[...(showPermanent ? permanent : []), ...(showTemporary ? temporary : [])].map(l => <li key={l.id} data-id="element-2622" data-mp="xfnvip5drwsp">
                  <Link to={monsterPath(l.monsterId)} className="flex h-full flex-col rounded-2xl border border-white/10 bg-[#0A1628] px-4 py-3 transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-2623" data-mp="tbzn648xcx6e">
                    <span className="flex flex-wrap items-baseline gap-x-2" data-id="element-2624" data-mp="12dpgjfmda66t">
                      <span className="font-semibold text-white" data-id="element-2625" data-mp="1lyzebbcb8sz0">{monsterName(w, l.monsterId)}</span>
                      {w.monsters.get(l.monsterId)?.level !== undefined && <span className="text-xs text-gray-500" data-id="element-2626" data-mp="1qx17wq7d4aoq">Nv. monstruo {w.monsters.get(l.monsterId)!.level}</span>}
                    </span>
                    <span className={`mt-0.5 text-sm ${l.kind === 'ordinary' ? 'text-gray-400' : 'text-[#D4AF37]/90'}`} data-id="element-2627" data-mp="1gzd0p0sklapo">{l.condition ?? 'Condición no documentada'}</span>
                  </Link>
                </li>)}
            </ul>}
        </WorldSection>

        {eventIds.length > 0 && showTemporary && <WorldSection id="eventos" title="Eventos e invasiones asociados" data-id="element-2628" data-mp="tbdaq3331oc5">
            <div className="flex flex-wrap gap-2" data-id="element-2629" data-mp="y8lrbz8l1kba">{eventIds.map(id => <EventChip key={id} w={w} eventId={id} data-id="element-2630" data-mp="y70we8icvzcw" />)}</div>
          </WorldSection>}

        <Disclosure title="Configuración técnica del mapa" data-id="element-2631" data-mp="kbgrigzgphoh">
          <StatList rows={[{
          label: 'experience_rate',
          value: m.experienceRate
        }, {
          label: 'item_drop_rate',
          value: m.itemDropRate
        }, {
          label: 'excellent_rate_per_million',
          value: m.excellentRatePerMillion
        }, {
          label: 'ancient_rate_per_million',
          value: m.ancientRatePerMillion
        }, {
          label: 'socket_items_allowed',
          value: m.socketItemsAllowed === undefined ? undefined : m.socketItemsAllowed ? 'Sí' : 'No'
        }, {
          label: 'ID del mapa',
          value: m.id
        }]} data-id="element-2632" data-mp="o9wbec08y9xo" />
          <p className="mt-3 text-xs text-gray-500" data-id="element-2633" data-mp="h15p8udc4tvz">Valores tal como figuran en maps.csv. No son probabilidades finales por monstruo.</p>
        </Disclosure>
      </div>
    </div>;
}